/**
 * F43-FC §6.1 — the ONE shared Skills panel for the admin user-detail page.
 * Extracted from `platform-users/[userId].tsx` (the old inline SkillsPanel +
 * SkillEditDialog) and widened to full CRUD:
 *   - Add sport (67-value SPORT_NAME_VALUES Select + claimed 1.0–5.0 + primary)
 *   - Full Edit (claimed 1.0–5.0 / effective 1.0–7.0 / source Select / primary
 *     toggle / collapsed "Advanced override" W-L-D + matches-played counters
 *     with a warning Banner + mandatory reason — D3/O3 fat-finger guardrail)
 *   - Soft Remove (preserves counters + rating history — D5)
 * All affordances gated by `users:skill_verify` via the `canEdit` prop. Backing
 * client: adminUsersApi.addSkill / upsertSkill / removeSkill. onReload =
 * reloadSkills (the page's null-guard-bypassing refetch).
 */
import { useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Card,
  Checkbox,
  Dialog,
  EmptyState,
  FormField,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@bravemove/ui-web';
import { SPORT_NAME_VALUES } from '@bravemove/types';
import { adminUsersApi, type AdminUserSkillRow } from '../../api/adminUsers';
import { translateApiError } from '../../auth/errors.js';

/** 1.0 … 7.0 in 0.5 steps — the admin-editable effective-skill range (F08/F31). */
const SKILL_VALUE_OPTIONS = Array.from({ length: 13 }, (_, i) => {
  const v = 1 + i * 0.5;
  return { value: String(v), label: v.toFixed(1) };
});

/** 1.0 … 5.0 in 0.5 steps — the user-claimed range (claimed CHECK ≤ 5.0). */
const CLAIMED_VALUE_OPTIONS = Array.from({ length: 9 }, (_, i) => {
  const v = 1 + i * 0.5;
  return { value: String(v), label: v.toFixed(1) };
});

const SPORT_OPTIONS = SPORT_NAME_VALUES.map((s) => ({ value: s, label: s }));

const SOURCE_OPTIONS = [
  { value: 'self', label: 'self' },
  { value: 'match_engine', label: 'match_engine' },
  { value: 'admin_verified', label: 'admin_verified' },
];

const SKILL_SOURCE_TONE: Record<string, 'success' | 'info' | 'neutral'> = {
  admin_verified: 'success',
  match_engine: 'info',
  self: 'neutral',
};

export function UserSkillsPanel({
  rows,
  userId,
  canEdit,
  onReload,
  onOpenSkillRatings,
}: {
  readonly rows: readonly AdminUserSkillRow[] | null;
  readonly userId: string;
  readonly canEdit: boolean;
  readonly onReload: () => void;
  /**
   * Cross-batch CTA — navigates to the F31 per-user skill-ratings page
   * (`/users/:userId/skill-ratings`, rating history + admin override). Rendered
   * in the tab header when supplied (gated `users:read` by the page).
   */
  readonly onOpenSkillRatings?: () => void;
}) {
  const { t } = useI18n();
  const [editing, setEditing] = useState<AdminUserSkillRow | null>(null);
  const [removing, setRemoving] = useState<AdminUserSkillRow | null>(null);
  const [adding, setAdding] = useState(false);

  if (rows === null) return <Skeleton className="h-24 w-full rounded-lg" />;

  const fmt = (v: number | null) => (v === null ? '—' : v.toFixed(1));
  const existingSports = new Set(rows.map((r) => r.sportName));

  return (
    <>
      <div className="flex items-center justify-between">
        <p className="text-[12px] text-ink-3">
          {t('admin:f43.skills.subtitle', { defaultValue: 'Per-sport ratings (F08/F31).' })}
        </p>
        <div className="flex items-center gap-2">
          {onOpenSkillRatings ? (
            <Button
              variant="ghost"
              size="sm"
              onClick={onOpenSkillRatings}
              data-testid="f43-skill-ratings-link"
            >
              {t('moderation:user.skill_ratings_link', { defaultValue: 'Skill ratings' })}
            </Button>
          ) : null}
          {canEdit ? (
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setAdding(true)}
              data-testid="f43-skill-add"
            >
              {t('admin:f43.skills.add', { defaultValue: 'Add sport' })}
            </Button>
          ) : null}
        </div>
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title={t('admin:f43.skills.empty', { defaultValue: 'No sports on this profile yet.' })}
        />
      ) : (
        <Card className="mt-3">
          <ul className="divide-y divide-outline" data-testid="f43-user-skills">
            {rows.map((r) => (
              <li
                key={r.sportName}
                className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
                data-testid={`f43-skill-${r.sportName}`}
              >
                <div className="min-w-0">
                  <p className="text-[13px] text-ink truncate">
                    {r.sportName}
                    {r.isPrimary ? (
                      <Badge tone="accent" size="sm" className="ms-2">
                        {t('admin:f43.skills.primary', { defaultValue: 'Primary' })}
                      </Badge>
                    ) : null}
                  </p>
                  <p className="font-mono text-[11px] text-ink-3">
                    {t('admin:f43.skills.claimed', { defaultValue: 'Claimed' })}{' '}
                    {fmt(r.claimedSkillValue)}
                    {'  ·  '}
                    {t('admin:f43.skills.record', { defaultValue: 'W/L/D' })} {r.wins}/{r.losses}/
                    {r.draws} ({r.matchesPlayed})
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <div className="text-end">
                    <p className="text-[11px] uppercase tracking-wide text-ink-3">
                      {t('admin:f43.skills.effective', { defaultValue: 'Effective' })}
                    </p>
                    <p className="text-[15px] font-semibold text-ink">
                      {fmt(r.effectiveSkillValue)}
                    </p>
                  </div>
                  <Badge tone={SKILL_SOURCE_TONE[r.skillSource] ?? 'neutral'} size="sm">
                    {r.skillSource}
                  </Badge>
                  {canEdit ? (
                    <>
                      <Button
                        variant="secondary"
                        size="xs"
                        onClick={() => setEditing(r)}
                        data-testid={`f43-skill-edit-${r.sportName}`}
                      >
                        {t('admin:f43.skills.edit', { defaultValue: 'Edit' })}
                      </Button>
                      <Button
                        variant="ghost"
                        size="xs"
                        onClick={() => setRemoving(r)}
                        data-testid={`f43-skill-remove-${r.sportName}`}
                      >
                        {t('admin:f43.skills.remove', { defaultValue: 'Remove' })}
                      </Button>
                    </>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {adding ? (
        <SkillAddDialog
          userId={userId}
          existingSports={existingSports}
          onClose={() => setAdding(false)}
          onSaved={() => {
            setAdding(false);
            onReload();
          }}
        />
      ) : null}
      {editing ? (
        <SkillEditDialog
          userId={userId}
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            onReload();
          }}
        />
      ) : null}
      {removing ? (
        <SkillRemoveDialog
          userId={userId}
          row={removing}
          onClose={() => setRemoving(null)}
          onSaved={() => {
            setRemoving(null);
            onReload();
          }}
        />
      ) : null}
    </>
  );
}

function SkillAddDialog({
  userId,
  existingSports,
  onClose,
  onSaved,
}: {
  readonly userId: string;
  readonly existingSports: ReadonlySet<string>;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const firstAvailable = SPORT_NAME_VALUES.find((s) => !existingSports.has(s)) ?? 'padel';
  const [sport, setSport] = useState<string>(firstAvailable);
  const [claimed, setClaimed] = useState('3');
  const [isPrimary, setIsPrimary] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await adminUsersApi.addSkill(userId, {
        sport,
        claimedSkillValue: Number(claimed),
        isPrimary,
      });
      toast.push({
        tone: 'success',
        title: t('admin:f43.skills.added', { defaultValue: 'Sport added.' }),
      });
      onSaved();
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      onConfirm={() => void handleConfirm()}
      title={t('admin:f43.skills.addTitle', { defaultValue: 'Add a sport' })}
      confirmLabel={t('admin:f43.skills.addConfirm', { defaultValue: 'Add sport' })}
      loading={saving}
    >
      <div className="space-y-3" data-testid="f43-skill-add-dialog">
        <FormField label={t('admin:f43.skills.sport', { defaultValue: 'Sport' })}>
          <Select
            value={sport}
            onChange={(e) => setSport(e.target.value)}
            options={SPORT_OPTIONS}
            data-testid="f43-skill-add-sport"
          />
        </FormField>
        <FormField label={t('admin:f43.skills.claimed', { defaultValue: 'Claimed (1.0–5.0)' })}>
          <Select
            value={claimed}
            onChange={(e) => setClaimed(e.target.value)}
            options={CLAIMED_VALUE_OPTIONS}
            data-testid="f43-skill-add-claimed"
          />
        </FormField>
        <Checkbox
          label={t('admin:f43.skills.primary', { defaultValue: 'Primary sport' })}
          checked={isPrimary}
          onChange={(e) => setIsPrimary(e.target.checked)}
          data-testid="f43-skill-add-primary"
        />
      </div>
    </Dialog>
  );
}

function SkillEditDialog({
  userId,
  row,
  onClose,
  onSaved,
}: {
  readonly userId: string;
  readonly row: AdminUserSkillRow;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [claimed, setClaimed] = useState(String(row.claimedSkillValue ?? 3));
  const [effective, setEffective] = useState(String(row.effectiveSkillValue ?? 4));
  const [source, setSource] = useState(row.skillSource);
  const [isPrimary, setIsPrimary] = useState(row.isPrimary);
  const [advanced, setAdvanced] = useState(false);
  const [wins, setWins] = useState(String(row.wins));
  const [losses, setLosses] = useState(String(row.losses));
  const [draws, setDraws] = useState(String(row.draws));
  const [matchesPlayed, setMatchesPlayed] = useState(String(row.matchesPlayed));
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const effectiveChanged = Number(effective) !== (row.effectiveSkillValue ?? 0);
  const countersChanged =
    Number(wins) !== row.wins ||
    Number(losses) !== row.losses ||
    Number(draws) !== row.draws ||
    Number(matchesPlayed) !== row.matchesPlayed;
  const reasonRequired = effectiveChanged || countersChanged;
  const reasonValid = !reasonRequired || reason.trim().length >= 10;

  const handleConfirm = async () => {
    if (!reasonValid) {
      toast.push({
        tone: 'danger',
        title: t('admin:f43.skills.reasonTooShort', {
          defaultValue: 'Reason must be at least 10 characters.',
        }),
      });
      return;
    }
    setSaving(true);
    try {
      await adminUsersApi.upsertSkill(userId, row.sportName, {
        claimedSkillValue: Number(claimed),
        ...(effectiveChanged ? { effectiveSkillValue: Number(effective) } : {}),
        ...(source !== row.skillSource && !effectiveChanged ? { skillSource: source } : {}),
        isPrimary,
        ...(countersChanged
          ? {
              wins: Number(wins),
              losses: Number(losses),
              draws: Number(draws),
              matchesPlayed: Number(matchesPlayed),
            }
          : {}),
        ...(reasonRequired ? { reason: reason.trim() } : {}),
      });
      toast.push({
        tone: 'success',
        title: t('admin:f43.skills.saved', { defaultValue: 'Skill updated.' }),
      });
      onSaved();
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      onConfirm={() => void handleConfirm()}
      title={t('admin:f43.skills.editTitle', {
        defaultValue: 'Edit skill — {sport}',
        sport: row.sportName,
      })}
      confirmLabel={t('admin:f43.skills.save', { defaultValue: 'Save skill' })}
      loading={saving}
    >
      <div className="space-y-3" data-testid="f43-skill-edit-dialog">
        <FormField label={t('admin:f43.skills.claimed', { defaultValue: 'Claimed (1.0–5.0)' })}>
          <Select
            value={claimed}
            onChange={(e) => setClaimed(e.target.value)}
            options={CLAIMED_VALUE_OPTIONS}
            data-testid="f43-skill-edit-claimed"
          />
        </FormField>
        <FormField label={t('admin:f43.skills.effective', { defaultValue: 'Effective (1.0–7.0)' })}>
          <Select
            value={effective}
            onChange={(e) => setEffective(e.target.value)}
            options={SKILL_VALUE_OPTIONS}
            data-testid="f43-skill-edit-effective"
          />
        </FormField>
        <FormField
          label={t('admin:f43.skills.source', { defaultValue: 'Skill source' })}
          // PU-12 — an effective-value change forces source='admin_verified'
          // server-side; a simultaneous manual source pick was silently dropped.
          // Lock the Select (showing the forced value) while effective differs.
          helper={
            effectiveChanged
              ? t('admin:f43.skills.sourceLocked', {
                  defaultValue:
                    "Changing the effective rating sets the source to 'admin_verified' automatically.",
                })
              : undefined
          }
        >
          <Select
            value={effectiveChanged ? 'admin_verified' : source}
            onChange={(e) => setSource(e.target.value)}
            options={SOURCE_OPTIONS}
            disabled={effectiveChanged}
            data-testid="f43-skill-edit-source"
          />
        </FormField>
        <Checkbox
          label={t('admin:f43.skills.primary', { defaultValue: 'Primary sport' })}
          checked={isPrimary}
          onChange={(e) => setIsPrimary(e.target.checked)}
          data-testid="f43-skill-edit-primary"
        />

        {/* Collapsed "Advanced override" — counters (D3/O3 fat-finger guardrail). */}
        <Button
          variant="ghost"
          size="xs"
          onClick={() => setAdvanced((v) => !v)}
          data-testid="f43-skill-advanced-toggle"
        >
          {advanced
            ? t('admin:f43.skills.advancedHide', { defaultValue: 'Hide advanced override' })
            : t('admin:f43.skills.advancedShow', {
                defaultValue: '⚠️ Advanced override (W/L/D + matches)',
              })}
        </Button>
        {advanced ? (
          <div
            className="space-y-3 rounded-md border border-outline p-3"
            data-testid="f43-skill-advanced"
          >
            <div data-testid="f43-skill-advanced-warning">
              <Banner tone="warning">
                {t('admin:f43.skills.advancedWarning', {
                  defaultValue:
                    'These counters are normally match-engine-derived. Editing them directly overrides match history. matchesPlayed cannot drop below the calibration snapshot.',
                })}
              </Banner>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <FormField label={t('admin:f43.skills.wins', { defaultValue: 'Wins' })}>
                <input
                  type="number"
                  min={0}
                  value={wins}
                  onChange={(e) => setWins(e.target.value)}
                  className="w-full rounded-md border border-outline bg-bg px-2 py-1 text-[13px] text-ink"
                  data-testid="f43-skill-edit-wins"
                />
              </FormField>
              <FormField label={t('admin:f43.skills.losses', { defaultValue: 'Losses' })}>
                <input
                  type="number"
                  min={0}
                  value={losses}
                  onChange={(e) => setLosses(e.target.value)}
                  className="w-full rounded-md border border-outline bg-bg px-2 py-1 text-[13px] text-ink"
                  data-testid="f43-skill-edit-losses"
                />
              </FormField>
              <FormField label={t('admin:f43.skills.draws', { defaultValue: 'Draws' })}>
                <input
                  type="number"
                  min={0}
                  value={draws}
                  onChange={(e) => setDraws(e.target.value)}
                  className="w-full rounded-md border border-outline bg-bg px-2 py-1 text-[13px] text-ink"
                  data-testid="f43-skill-edit-draws"
                />
              </FormField>
              <FormField label={t('admin:f43.skills.matches', { defaultValue: 'Matches played' })}>
                <input
                  type="number"
                  min={0}
                  value={matchesPlayed}
                  onChange={(e) => setMatchesPlayed(e.target.value)}
                  className="w-full rounded-md border border-outline bg-bg px-2 py-1 text-[13px] text-ink"
                  data-testid="f43-skill-edit-matches"
                />
              </FormField>
            </div>
          </div>
        ) : null}

        {reasonRequired ? (
          <FormField label={t('admin:f43.skills.reason', { defaultValue: 'Reason (audit log)' })}>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
              placeholder={t('admin:f43.skills.reasonPlaceholder', {
                defaultValue: 'Why are you adjusting this rating/record? (min 10 chars)',
              })}
              data-testid="f43-skill-edit-reason"
            />
          </FormField>
        ) : null}
      </div>
    </Dialog>
  );
}

function SkillRemoveDialog({
  userId,
  row,
  onClose,
  onSaved,
}: {
  readonly userId: string;
  readonly row: AdminUserSkillRow;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}) {
  const { t } = useI18n();
  const toast = useToast();
  const [saving, setSaving] = useState(false);

  const handleConfirm = async () => {
    setSaving(true);
    try {
      await adminUsersApi.removeSkill(userId, row.sportName);
      toast.push({
        tone: 'success',
        title: t('admin:f43.skills.removed', { defaultValue: 'Sport removed.' }),
      });
      onSaved();
    } catch (err) {
      toast.push({ tone: 'danger', title: translateApiError(err, t) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open
      onClose={onClose}
      onConfirm={() => void handleConfirm()}
      title={t('admin:f43.skills.removeTitle', {
        defaultValue: 'Remove {sport}?',
        sport: row.sportName,
      })}
      description={t('admin:f43.skills.removeBody', {
        defaultValue:
          'Soft-removes this sport from the profile. The match record + rating history are preserved and the sport can be re-added later.',
      })}
      confirmLabel={t('admin:f43.skills.removeConfirm', { defaultValue: 'Remove sport' })}
      loading={saving}
    >
      <div data-testid="f43-skill-remove-dialog" />
    </Dialog>
  );
}
